import React, { useContext } from "react";
import {Pressable, View} from "react-native";
import {Switch} from "react-native-paper";
import {ModalM} from "../../../components/modal/ModalM";
import {ListGroupM} from "../../../components/list/ListGroupM";
import {ListItemM} from "../../../components/list/ListItemM";
import TitleM from "../../../components/text/TitleM";
import TextM from "../../../components/text/TextM";
import {theme} from "../../../theme/theme";
import { Tabs, TabScreen, TabsProvider } from 'react-native-paper-tabs';
import FontAwesome5 from "react-native-vector-icons/FontAwesome5";
import { DimensionsContext } from "@common/utils/mobileUtils";
import { usePermissions } from "@common/livekit/components/PermissionBanner";
import MicrophoneLevelIndicatorM from "../../../components/microphone/MicrophoneLevelIndicatorM";


/**
 * Translates English device labels to French
 */
const translateDeviceLabel = (label: string): string => {
    if (!label) return label

    // Translate common device type keywords
    let translated = label
        .replace(/Built-in/gi, "Intégré")
        .replace(/Virtual/gi, "Virtuel")
        .replace(/Default/gi, "Par défaut")
        .replace(/Microphone/gi, "Microphone")
        .replace(/Camera/gi, "Caméra")
        .replace(/Speaker/gi, "Haut-parleur")
        .replace(/Headphones/gi, "Écouteurs")
        .replace(/Headset/gi, "Casque")

    return translated
}

export default function MediaDeviceSelectModal({
                                                   isOpen,
                                                   handleShowModal,
                                                   devices,
                                                   activeDeviceId,
                                                   handleActiveDeviceChange,
                                                   title,
                                                   includeAudioOutput,
                                                   audioOutputDevices = [],
                                                   activeOutputDeviceId,
                                                   isSoundMode,
                                                   krispEnabled,
                                                   krispPending,
                                                   onKrispToggle,
                                                   videoInputDevices = [],
                                                   activeVideoInputId,
                                                   onOpenHelp,
                                                   defaultTabIndex = 0,
                                               }) {

    const dimensions = useContext(DimensionsContext);
    const { permissionsGranted } = usePermissions();

    const inputItems = devices.map((device, index) => {
        const deviceId = device.deviceId ?? device
        const rawLabel = device.label ?? device
        const translatedLabel = translateDeviceLabel(rawLabel)

        // Device is "default" if it's the first one or if deviceId contains "default"
        const isDefault = index === 0 || deviceId.toLowerCase().includes("default")
        const description = isDefault ? "Par défaut du système" : undefined

        return {
            ...device,
            value: deviceId,
            label: translatedLabel,
            description: description,
            kind: 'audioinput' as MediaDeviceKind
        }
    })

    const outputItems = audioOutputDevices.map((device, index) => {
        const deviceId = device.deviceId ?? device
        const rawLabel = device.label ?? device
        const translatedLabel = translateDeviceLabel(rawLabel)

        // Device is "default" if it's the first one or if deviceId contains "default"
        const isDefault = index === 0 || deviceId.toLowerCase().includes("default")
        const description = isDefault ? "Par défaut du système" : undefined

        return {
            ...device,
            value: deviceId,
            label: translatedLabel,
            description: description,
            kind: 'audiooutput' as MediaDeviceKind
        }
    })

    const videoItems = videoInputDevices.map((device, index) => {
        const deviceId = device.deviceId ?? device
        const rawLabel = device.label ?? device
        const translatedLabel = translateDeviceLabel(rawLabel)
        const isDefault = index === 0 || deviceId.toLowerCase().includes("default")
        const description = isDefault ? "Par défaut du système" : undefined

        return {
            ...device,
            value: deviceId,
            label: translatedLabel,
            description: description,
            kind: 'videoinput' as MediaDeviceKind
        }
    })

    const showTabs = includeAudioOutput || videoItems.length > 0
    const items = includeAudioOutput ? [...inputItems, ...outputItems] : inputItems


    // Render device list
    const renderDeviceList = (items: any[], activeId: string, deviceKind: MediaDeviceKind) => {
        if (items.length === 0) {
            return (
                <TextM
                    color={theme.colors.grey}
                    style={{ textAlign: "center", padding: 20 }}
                >
                    Aucun appareil disponible
                </TextM>
            );
        }

        if (items.length === 1) {
            return (
                <View style={{ width: "100%" }}>
                    <ListGroupM
                        options={items}
                        style={{ marginBottom: 20 }}
                        itemHeight={70}
                        renderItem={(item) => (
                            <ListItemM
                                isFirst={true}
                                isLast={true}
                                onPress={() => handleShowModal()}
                                itemHeight={70}
                                selected={true}
                                option={item.item}
                                disabled={false}
                            />
                        )}
                    />
                </View>
            );
        }

        return (
            <ListGroupM
                options={items}
                style={{ marginBottom: 20 }}
                itemHeight={70}
                renderItem={(item) => (
                    <ListItemM
                        isFirst={item.index === 0}
                        isLast={item.index === items.length - 1}
                        onPress={() => handleActiveDeviceChange(item.item.value, deviceKind)}
                        itemHeight={70}
                        selected={item.item.value === activeId}
                        option={item.item}
                    />
                )}
            />
        );
    };

    return (
        <ModalM
            visible={isOpen}
            onDismiss={handleShowModal}
            dismissable={true}
            withExitButton={true}
            style={{
                maxHeight: "80%",
                padding: 20,
                alignItems: "center",
                width: "85%",
                maxWidth: dimensions?.width,
            }}
        >
            <TitleM>{title}</TitleM>

            {/* Warning banner for concentrated mode */}
            {!isSoundMode && includeAudioOutput && (
                <View
                    style={{
                        width: "100%",
                        backgroundColor: theme.colors.purple,
                        padding: 15,
                        marginTop: 15,
                        borderRadius: 10,
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <FontAwesome5
                        name="volume-mute"
                        size={16}
                        color={theme.colors.white}
                        style={{ marginRight: 10 }}
                    />
                    <TextM
                        color={theme.colors.white}
                        fontWeight="SemiBold"
                        fontSize={11}
                        wrap
                        style={{ flex: 1 }}
                    >
                        En mode concentré, l'audio est coupé. Seul ton tuteur peut te remettre en mode dialogue. En attendant, tu peux quand même configurer tes appareils.
                    </TextM>
                </View>
            )}

            <View
                style={{
                    marginBottom: 10,
                    marginTop: 20,
                    width: "100%",
                    flex: 1,
                }}
            >
                {items.length === 0 ? (
                    <TextM
                        color={theme.colors.grey}
                        style={{ textAlign: "center", padding: 20 }}
                    >
                        Aucun appareil disponible
                    </TextM>
                ) : showTabs ? (
                    // Show tabs for input, output, and video
                    <TabsProvider defaultIndex={defaultTabIndex}>
                        <Tabs
                            style={{
                                backgroundColor: theme.colors.white,
                                width: "100%",
                            }}
                            disableSwipe={false}
                            mode="fixed"
                            uppercase={false}
                            showTextLabel={true}
                            theme={{
                                colors: {
                                    primary: theme.colors.primary,
                                },
                                fonts: {
                                    labelLarge: {
                                        fontFamily: "Montserrat-SemiBold",
                                        fontSize: 14,
                                        letterSpacing: 0,
                                        lineHeight: 20,
                                        fontWeight: "600",
                                    },
                                },
                            }}
                            indicatorStyle={{
                                backgroundColor: theme.colors.primary,
                                height: 3,
                            }}
                            tabStyle={{
                                paddingVertical: 12,
                            }}
                        >
                            <TabScreen
                                label="Microphone"
                                icon="microphone"
                            >
                                <View style={{ paddingTop: 20 }}>
                                    {/* Microphone Level Indicator */}
                                    <MicrophoneLevelIndicatorM
                                        permissionsGranted={permissionsGranted}
                                        isSoundMode={isSoundMode}
                                        hideHelpButton={true}
                                        style={{
                                            alignSelf: "center",
                                            marginBottom: 20,
                                        }}
                                    />

                                    {/* Krisp Noise Cancellation Toggle */}
                                    {onKrispToggle && (
                                        <View
                                            style={{
                                                backgroundColor: theme.colors.lighter,
                                                padding: 15,
                                                marginBottom: 15,
                                                borderRadius: 10,
                                                flexDirection: "row",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <View style={{ flex: 1, marginRight: 15 }}>
                                                <TextM
                                                    fontWeight="SemiBold"
                                                    fontSize={14}
                                                    style={{ marginBottom: 4 }}
                                                >
                                                    Supprimer les bruits de fond
                                                </TextM>
                                                <TextM
                                                    color={theme.colors.grey}
                                                    fontSize={12}
                                                >
                                                    Réduis les bruits autour de toi
                                                </TextM>
                                            </View>
                                            <Switch
                                                value={krispEnabled}
                                                onValueChange={onKrispToggle}
                                                disabled={krispPending}
                                                color={theme.colors.primary}
                                            />
                                        </View>
                                    )}

                                    {renderDeviceList(inputItems, activeDeviceId, 'audioinput')}
                                </View>
                            </TabScreen>
                            <TabScreen
                                label="Haut-parleur"
                                icon="volume-high"
                            >
                                <View style={{ paddingTop: 20 }}>
                                    {renderDeviceList(outputItems, activeOutputDeviceId, 'audiooutput')}
                                </View>
                            </TabScreen>
                            <TabScreen
                                label="Caméra"
                                icon="video"
                            >
                                <View style={{ paddingTop: 20 }}>
                                    {renderDeviceList(videoItems, activeVideoInputId, 'videoinput')}
                                </View>
                            </TabScreen>
                        </Tabs>
                    </TabsProvider>
                ) : (
                    renderDeviceList(items, activeDeviceId, items[0]?.kind || 'videoinput')
                )}
            </View>

            {onOpenHelp && (
                <Pressable
                    onPress={() => {
                        handleShowModal()
                        onOpenHelp()
                    }}
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        paddingVertical: 12,
                        paddingHorizontal: 16,
                        backgroundColor: theme.colors.primaryLighter,
                        borderRadius: 10,
                        marginTop: 8,
                        width: "100%",
                    }}
                >
                    <FontAwesome5 name="stethoscope" size={14} color={theme.colors.primary} />
                    <TextM fontSize={13} fontWeight="SemiBold" color={theme.colors.primary}>
                        Un problème ?
                    </TextM>
                </Pressable>
            )}
        </ModalM>
    );
}
